import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const PropertyForm = forwardRef(({ onChange, subscriptionTier = 'free', initialData = null }, ref) => {
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    state: '',
    zipCode: '',
    friendlyName: '',
    propertyType: '',
    listingPrice: '',
    commissionPercentage: ''
  });

  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Prepopulate form when initialData is provided
  useEffect(() => {
    if (initialData) {
      setFormData({
        address: initialData?.address || '',
        city: initialData?.city || '',
        state: initialData?.state || '',
        zipCode: initialData?.zipCode || '',
        friendlyName: initialData?.friendlyName || '',
        propertyType: initialData?.propertyType || initialData?.property_type || '',
        listingPrice: initialData?.listingPrice || initialData?.listing_price || '',
        commissionPercentage: initialData?.commissionPercentage || initialData?.commission_percentage || ''
      });
    }
  }, [initialData]);

  const propertyTypeOptions = [
    { value: 'single-family', label: 'Single Family Home' },
    { value: 'condo', label: 'Condominium' },
    { value: 'townhouse', label: 'Townhouse' },
    { value: 'multi-family', label: 'Multi-Family' },
    { value: 'land', label: 'Land/Lot' },
    { value: 'commercial', label: 'Commercial' }
  ];

  const stateOptions = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    setFormData(updatedFormData);
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [name]: true }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    setFormData(updatedFormData);
    
    // Mark field as touched
    setTouched(prev => ({ ...prev, [name]: true }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Notify parent of form data changes
  useEffect(() => {
    if (validateForm(true)) {
      onChange?.(formData);
    }
  }, [formData]);

  const validateForm = (silent = false) => {
    const newErrors = {};

    if (!formData?.address?.trim()) {
      newErrors.address = 'Street address is required';
    }

    if (!formData?.city?.trim()) {
      newErrors.city = 'City is required';
    }

    if (!formData?.state) {
      newErrors.state = 'State is required';
    }

    if (!formData?.zipCode?.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    } else if (!/^\d{5}(-\d{4})?$/?.test(formData?.zipCode)) {
      newErrors.zipCode = 'Invalid ZIP code format';
    }

    if (!formData?.propertyType) {
      newErrors.propertyType = 'Property type is required';
    }

    if (!formData?.listingPrice?.trim()) {
      newErrors.listingPrice = 'Listing price is required';
    } else if (isNaN(parseFloat(formData?.listingPrice?.toString()?.replace(/,/g, '')))) {
      newErrors.listingPrice = 'Invalid price format';
    }

    if (formData?.commissionPercentage?.trim()) {
      const commissionValue = parseFloat(formData?.commissionPercentage);
      if (isNaN(commissionValue)) {
        newErrors.commissionPercentage = 'Invalid percentage format';
      } else if (commissionValue < 0 || commissionValue > 100) {
        newErrors.commissionPercentage = 'Commission must be between 0 and 100';
      }
    }

    if (!silent) {
      setErrors(newErrors);
      // Mark all fields as touched when validating
      const allTouched = Object.keys(formData)?.reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {});
      setTouched(allTouched);
    }
    return Object.keys(newErrors)?.length === 0;
  };

  // Expose validation method to parent via ref
  useImperativeHandle(ref, () => ({
    validate: () => validateForm(false),
    getData: () => formData
  }));

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-md">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-primary/10 flex items-center justify-center">
            <Icon name="MapPin" size={20} className="text-primary" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground">Property Address</h3>
            <p className="text-sm text-muted-foreground">Enter the complete property location</p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5">
          <Input
            label="Street Address"
            name="address"
            type="text"
            placeholder="123 Main Street"
            value={formData?.address}
            onChange={handleInputChange}
            error={errors?.address}
            required
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
            <Input
              label="City"
              name="city"
              type="text"
              placeholder="Los Angeles"
              value={formData?.city}
              onChange={handleInputChange}
              error={errors?.city}
              required
            />

            <Select
              label="State"
              options={stateOptions}
              value={formData?.state}
              onChange={(value) => handleSelectChange('state', value)}
              placeholder="Select state"
              error={errors?.state}
              searchable
              required
            />
          </div>

          <Input
            label="ZIP Code"
            name="zipCode"
            type="text"
            placeholder="90001"
            value={formData?.zipCode}
            onChange={handleInputChange}
            error={errors?.zipCode}
            required
          />

          <Input
            label="Property Name (Optional)"
            name="friendlyName"
            type="text"
            placeholder="Enter a friendly name for this property"
            value={formData?.friendlyName}
            onChange={handleInputChange}
            description="If not provided, the street address will be used"
          />
        </div>
      </div>
      <div className="bg-card rounded-lg p-4 md:p-6 lg:p-8 shadow-elevation-md">
        <div className="flex items-center gap-3 mb-4 md:mb-6">
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-lg bg-accent/10 flex items-center justify-center">
            <Icon name="Home" size={20} className="text-accent" />
          </div>
          <div>
            <h3 className="text-lg md:text-xl font-semibold text-foreground">Property Details</h3>
            <p className="text-sm text-muted-foreground">Provide key property information</p>
          </div>
        </div>

        <div className="space-y-4 md:space-y-5">
          <Select
            label="Property Type"
            options={propertyTypeOptions}
            value={formData?.propertyType}
            onChange={(value) => handleSelectChange('propertyType', value)}
            placeholder="Select property type"
            error={errors?.propertyType}
            required
          />

          <Input
            label="Listing Price"
            name="listingPrice"
            type="text"
            placeholder="500000"
            value={formData?.listingPrice}
            onChange={handleInputChange}
            error={errors?.listingPrice}
            description="Enter amount in USD without symbols"
          />

          <Input
            label="Commission Percentage (Optional)"
            name="commissionPercentage"
            type="text"
            placeholder="0.0"
            value={formData?.commissionPercentage}
            onChange={handleInputChange}
            error={errors?.commissionPercentage}
            description="Enter commission percentage (0-100)"
          />
        </div>
      </div>
    </div>
  );
});

export default PropertyForm;